/**
 * Editorial Community page content.  The server stores overrides for this
 * contract, while this value remains the deterministic source of truth when
 * no configuration exists (or a configuration cannot be projected safely).
 */
export const COMMUNITY_CATEGORIES = [
  "All News",
  "Insights",
  "Events",
  "Discussions",
  "Resources",
  "Member Stories",
] as const;
export type CommunityCategory = (typeof COMMUNITY_CATEGORIES)[number];

export const COMMUNITY_ARTICLE_ICONS = ["message", "sparkles", "calendar", "handshake"] as const;
export type CommunityArticleIcon = (typeof COMMUNITY_ARTICLE_ICONS)[number];

export type CommunityArticle = {
  category: string;
  title: string;
  copy: string;
  action: string;
  href: string;
  icon: CommunityArticleIcon;
  style: "plum" | "gold" | "cyan" | "pink";
  visible: boolean;
};

export type CommunityPageContent = {
  dashboardEyebrow: string;
  dashboardTitle: string;
  dashboardDescription: string;
  memberLabel: string;
  eventLabel: string;
  eventAction: string;
  sponsorEyebrow: string;
  sponsorTitle: string;
  sponsorDescription: string;
  sponsorSlugs: string[];
  customizeLabel: string;
  newsTitle: string;
  filterLabel: string;
  categories: string[];
  articles: CommunityArticle[];
  sidebar: {
    stayConnectedTitle: string;
    stayConnectedDescription: string;
    subscribeLabel: string;
    newsletterNote: string;
    eventsTitle: string;
    eventsViewAllLabel: string;
    eventsEmptyLabel: string;
    eventsCalendarLabel: string;
  };
};

export const DEFAULT_COMMUNITY_PAGE_CONTENT: CommunityPageContent = {
  dashboardEyebrow: "The CHI Alliance",
  dashboardTitle: "What’s happening in our community",
  dashboardDescription: "Explore news, events, and purposeful next steps from across The CHI Alliance.",
  memberLabel: "Member Spotlight",
  eventLabel: "Event highlight",
  eventAction: "View highlight reel",
  sponsorEyebrow: "Sponsor Highlight",
  sponsorTitle: "Meet the partners strengthening our circle.",
  sponsorDescription: "Explore the organizations supporting connection, learning, and meaningful progress across The CHI Alliance.",
  sponsorSlugs: ["feng-shui-journey", "safetywing", "tebeau-group"],
  customizeLabel: "Customize",
  newsTitle: "Community news",
  filterLabel: "Filter by:",
  categories: [...COMMUNITY_CATEGORIES],
  articles: [
    {
      category: "Discussions",
      title: "Find the people who move work forward.",
      copy: "Explore visible member profiles and discover shared professional interests across The CHI Alliance.",
      action: "Explore directory",
      href: "/member-directory",
      icon: "message",
      style: "plum",
      visible: true,
    },
    {
      category: "Insights",
      title: "A connection-first member experience.",
      copy: "See the spaces, events, and benefits designed to turn introductions into long-term momentum.",
      action: "Explore membership",
      href: "/membership",
      icon: "sparkles",
      style: "gold",
      visible: true,
    },
    {
      category: "Events",
      title: "Make room for the next conversation.",
      copy: "Browse upcoming gatherings built around learning, connection, and collaboration.",
      action: "View events",
      href: "/calendar-of-events",
      icon: "calendar",
      style: "cyan",
      visible: true,
    },
    {
      category: "Member Stories",
      title: "The shared purpose behind the community.",
      copy: "Learn why The CHI Alliance exists and how its values make authentic progress possible.",
      action: "Our mission",
      href: "/mission-and-values",
      icon: "handshake",
      style: "pink",
      visible: true,
    },
    {
      category: "Resources",
      title: "Resources for meaningful progress.",
      copy: "Find practical tools and ideas shared by people building better ways to work together.",
      action: "Browse resources",
      href: "/resources",
      icon: "sparkles",
      style: "gold",
      visible: true,
    },
    {
      category: "Events",
      title: "Gather with intention.",
      copy: "Join the next Community gathering and make space for a useful new connection.",
      action: "View events",
      href: "/calendar-of-events",
      icon: "calendar",
      style: "cyan",
      visible: true,
    },
  ],
  sidebar: {
    stayConnectedTitle: "Stay connected",
    stayConnectedDescription: "Get a monthly digest of Community news and upcoming events.",
    subscribeLabel: "Subscribe to Updates",
    newsletterNote: "Confirm by email. Unsubscribe anytime.",
    eventsTitle: "Upcoming events",
    eventsViewAllLabel: "See the full calendar",
    eventsEmptyLabel: "New Community events will appear here.",
    eventsCalendarLabel: "View event",
  },
};

export type CommunityPageProjection = CommunityPageContent;